import { computed, effect, inject, Injectable, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import { Attendee, Meeting } from '@aws-sdk/client-chime-sdk-meetings';
import { TranslateService } from '@ngx-translate/core';
import {
  AudioProfile,
  ConsoleLogger,
  DataMessage,
  DefaultDeviceController,
  DefaultMeetingSession,
  DefaultVideoTransformDevice,
  DeviceChangeObserver,
  LogLevel,
  MeetingSession,
  MeetingSessionConfiguration,
  VideoFxConfig,
  VideoFxProcessor,
  VideoTileState,
} from 'amazon-chime-sdk-js';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { MediaDevicePermissionWatcherService } from 'src/app/media-device-permission-watcher.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { ChimeCallService } from './chime-call.service';
import { MeetingTranscriptionService } from './meeting-transcription.service';
import { VideoCallStateService } from './video-call-state.service';

export interface RosterAttendee {
  chimeAttendeeId: string;
  externalUserId: string;
  username: string;
  isMuted: boolean;
  isPstn?: boolean;
}
export interface ChatMessage {
  timestamp: number;
  senderName: string;
  senderId: string;
  message: string;
}

export interface Participant {
  attendee: RosterAttendee;
  tileState: VideoTileState | null;
  isLocal: boolean;
}
const CHAT_DATA_MESSAGE_TOPIC = 'chat';

@Injectable({
  providedIn: 'root',
})
export class ChimeService {
  private chimeCallService = inject(ChimeCallService);
  private authService = inject(AuthService);
  private toast = inject(ToastService);
  private permissionWatcher = inject(MediaDevicePermissionWatcherService);
  private videoCallStateService = inject(VideoCallStateService);
  private translate = inject(TranslateService);
  private contentStateService = inject(ContentStateService);
  private auth = inject(Auth);
  private meetingTranscriptionService = inject(MeetingTranscriptionService);
  private logger = new ConsoleLogger('ChimeLogs', LogLevel.INFO);
  private deviceController: DefaultDeviceController;
  private audioCtx: AudioContext | null = null;

  #meetingAudioElement: HTMLAudioElement | null = null;

  #previewVideoElement: HTMLVideoElement | null = null;
  #videoPreviewIsActive: WritableSignal<boolean> = signal(false);

  #meetingSession: WritableSignal<MeetingSession | null> = signal(null);
  #localTileId: WritableSignal<number | null> = signal(null);
  #remoteTiles: WritableSignal<VideoTileState[]> = signal([]);
  #roster: WritableSignal<RosterAttendee[]> = signal([]);
  #isMuted: WritableSignal<boolean> = signal(true);
  #isCameraEnabled: WritableSignal<boolean> = signal(false);
  #isAudioLocked: WritableSignal<boolean> = signal(false);
  #isCameraLocked: WritableSignal<boolean> = signal(false);
  #isScreenShareLocked: WritableSignal<boolean> = signal(false);
  #isChatLocked: WritableSignal<boolean> = signal(false);
  #isWaitingRoomEnabled: WritableSignal<boolean> = signal(false);
  #tenantId: WritableSignal<string> = signal('');
  #chatMessages: WritableSignal<ChatMessage[]> = signal([]);
  #screenShareTile: WritableSignal<VideoTileState | null> = signal(null);
  #isVideoFxActive: WritableSignal<boolean> = signal(false);
  #videoFxProcessor: WritableSignal<VideoFxProcessor | null> = signal(null);
  #videoTransformDevice: WritableSignal<DefaultVideoTransformDevice | null> = signal(null);

  // Speaker detection signals
  #speakerVolumes: WritableSignal<Map<string, number>> = signal(new Map());
  #activeSpeakerId: WritableSignal<string | null> = signal(null);
  #activeSpeakerInterval: any = null;
  isGeminiLiveActive = computed(() => this.meetingTranscriptionService.isGeminiLiveActive());

  #meetingStartTime: WritableSignal<Date | null> = signal(null);
  meetingDurationText: WritableSignal<string> = signal('00:00');
  private durationInterval: any = null;

  #isRecording: WritableSignal<boolean> = signal(false);
  #isDialing: WritableSignal<boolean> = signal(false);
  #isHost: WritableSignal<boolean> = signal(false);
  #hostAttendeeIds: WritableSignal<Set<string>> = signal(new Set());

  // Device Signals
  #videoInputDevices: WritableSignal<MediaDeviceInfo[]> = signal([]);
  #audioInputDevices: WritableSignal<MediaDeviceInfo[]> = signal([]);
  #audioOutputDevices: WritableSignal<MediaDeviceInfo[]> = signal([]);
  #selectedVideoInputId: WritableSignal<string | null> = signal(null);
  #selectedAudioInputId: WritableSignal<string | null> = signal(null);
  #selectedAudioOutputId: WritableSignal<string | null> = signal(null);

  #audioQuality: WritableSignal<'standard' | 'high-speech' | 'high-music'> = signal('high-speech');
  #videoQuality: WritableSignal<'standard' | 'hd' | 'fhd'> = signal('standard');
  #echoCancellation: WritableSignal<boolean> = signal(true);
  #noiseSuppression: WritableSignal<boolean> = signal(true);
  #autoGainControl: WritableSignal<boolean> = signal(true);

  public isMuted = this.#isMuted.asReadonly();
  public isCameraEnabled = this.#isCameraEnabled.asReadonly();
  public isAudioLocked = this.#isAudioLocked.asReadonly();
  public isCameraLocked = this.#isCameraLocked.asReadonly();
  public isScreenShareLocked = this.#isScreenShareLocked.asReadonly();
  public isChatLocked = this.#isChatLocked.asReadonly();
  public isWaitingRoomEnabled = this.#isWaitingRoomEnabled.asReadonly();
  public tenantId = this.#tenantId.asReadonly();
  public hostAttendeeIds = this.#hostAttendeeIds.asReadonly();
  public remoteTiles = this.#remoteTiles.asReadonly();
  public roster = this.#roster.asReadonly();
  public isMeetingActive = computed(() => this.#meetingSession() !== null);
  public chatMessages = this.#chatMessages.asReadonly();
  #unreadCount: WritableSignal<number> = signal(0);
  public unreadCount = this.#unreadCount.asReadonly();
  public isChatVisible = signal(false);
  public screenShareTile = this.#screenShareTile.asReadonly();
  public isScreenSharing = computed(() => this.#screenShareTile() !== null);
  public isVideoFxActive = this.#isVideoFxActive.asReadonly();
  public activeSpeakerId = this.#activeSpeakerId.asReadonly();
  public isRecording = this.#isRecording.asReadonly();
  public isDialing = this.#isDialing.asReadonly();
  public isHost = this.#isHost.asReadonly();
  public videoPreviewIsActive = this.#videoPreviewIsActive.asReadonly();
  public chatPaneState = this.videoCallStateService.chatPaneState;
  public meetingDuration = this.meetingDurationText.asReadonly();
  public drawerOpened = this.videoCallStateService.drawerOpened;

  #isBlurEnabled: WritableSignal<boolean> = signal(false);
  public isBlurEnabled = this.#isBlurEnabled.asReadonly();

  #blurStrength: WritableSignal<'low' | 'medium' | 'high'> = signal('medium');
  public blurStrength = this.#blurStrength.asReadonly();

  public audioQuality = this.#audioQuality.asReadonly();
  public videoQuality = this.#videoQuality.asReadonly();
  public echoCancellation = this.#echoCancellation.asReadonly();
  public noiseSuppression = this.#noiseSuppression.asReadonly();
  public autoGainControl = this.#autoGainControl.asReadonly();

  public attendeesToRemove = signal<string[]>([]);

  public participants: Signal<Participant[]> = computed(() => {
    const attendeesToRemove = this.attendeesToRemove();
    console.log('#Roster', this.#roster());
    const roster = this.#roster();
    const tiles = this.#remoteTiles();
    const localAttendeeId = this.#meetingSession()?.configuration.credentials?.attendeeId;
    if (!localAttendeeId) return [];
    return roster
      .filter((attendee) => {
        if (attendeesToRemove.includes(attendee.externalUserId)) {
          return false;
        }
        return !attendee.externalUserId.startsWith('aws:MediaPipeline');
      }) // filter out recording agent
      .map((attendee) => {
        const tile = tiles.find((t) => t.boundAttendeeId === attendee.chimeAttendeeId && !t.isContent) ?? null;
        const isLocal = attendee.chimeAttendeeId === localAttendeeId;
        return {
          attendee: isLocal ? { ...attendee, isMuted: this.#isMuted() } : attendee,
          tileState: tile,
          isLocal,
        };
      });
  });

  localParticipant = computed(() => this.participants().find((p) => p.isLocal));
  remoteParticipants = computed(() => this.participants().filter((p) => !p.isLocal));
  // Combines local and remote participants for the presentation sidebar
  allParticipants = computed(() => [this.localParticipant(), ...this.remoteParticipants()].filter((p): p is Participant => !!p));
  localUserId = computed(() => this.localParticipant()?.attendee.externalUserId || '');

  // Public device signals
  public videoInputDevices = this.#videoInputDevices.asReadonly();
  public audioInputDevices = this.#audioInputDevices.asReadonly();
  public audioOutputDevices = this.#audioOutputDevices.asReadonly();
  public selectedVideoInputId = this.#selectedVideoInputId.asReadonly();
  public selectedAudioInputId = this.#selectedAudioInputId.asReadonly();
  public selectedAudioOutputId = this.#selectedAudioOutputId.asReadonly();

  private aloneTimer: any = null;

  private clearAloneTimer(): void {
    if (this.aloneTimer) {
      clearTimeout(this.aloneTimer);
      this.aloneTimer = null;
    }
  }

  constructor() {
    this.deviceController = new DefaultDeviceController(this.logger);
    this.addDeviceChangeObserver();
    this.initAudioContextOnGesture();

    effect(() => {
      const active = this.isMeetingActive();
      const participants = this.participants();
      const localHost = this.isHost();
      const hosts = this.hostAttendeeIds();

      if (!active) {
        this.clearAloneTimer();
        return;
      }

      // Check if the user is alone in the meeting (excluding recording agents, which are already filtered in participants())
      const isAlone = participants.length <= 1;

      // Check if there is any host in the meeting
      const hostPresent = localHost || participants.some((p) => hosts.has(p.attendee.chimeAttendeeId));

      // Conditions for auto-leaving:
      // 1. User is a guest and is alone -> Auto-leave after 5 minutes
      // 2. User is a guest and no host is present -> Auto-leave after 5 minutes
      // (Hosts are exempt from the auto-leave timer when alone)
      const shouldTriggerTimer = !localHost && (isAlone || !hostPresent);

      if (shouldTriggerTimer) {
        if (!this.aloneTimer) {
          console.log(`[ChimeService] Starting auto-leave timer. isAlone: ${isAlone}, hostPresent: ${hostPresent}`);
          this.aloneTimer = setTimeout(
            () => {
              console.log('[ChimeService] Auto-leaving meeting due to inactivity/alone state.');
              this.toast.showWithoutHeader({
                level: 'info',
                text: this.translate.instant('VideoCall.Toast.AutoLeftInactivity'),
              });
              this.leaveMeeting();
            },
            5 * 60 * 1000
          ); // 5 minutes
        }
      } else {
        if (this.aloneTimer) {
          console.log('[ChimeService] Clearing auto-leave timer: meeting is no longer empty/host returned.');
          this.clearAloneTimer();
        }
      }
    });

    window.addEventListener('beforeunload', (event) => {
      if (this.isMeetingActive()) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    });

    // Read saved audio settings
    const savedAudioQuality = localStorage.getItem('selectedAudioQuality') as 'standard' | 'high-speech' | 'high-music' | null;
    if (savedAudioQuality) this.#audioQuality.set(savedAudioQuality);

    const savedVideoQuality = localStorage.getItem('selectedVideoQuality') as 'standard' | 'hd' | 'fhd' | null;
    if (savedVideoQuality) this.#videoQuality.set(savedVideoQuality);

    const savedEC = localStorage.getItem('echoCancellation');
    if (savedEC !== null) this.#echoCancellation.set(savedEC === 'true');

    const savedNS = localStorage.getItem('noiseSuppression');
    if (savedNS !== null) this.#noiseSuppression.set(savedNS === 'true');

    const savedAGC = localStorage.getItem('autoGainControl');
    if (savedAGC !== null) this.#autoGainControl.set(savedAGC === 'true');

    const savedBlurEnabled = localStorage.getItem('selectedBlurEnabled');
    if (savedBlurEnabled !== null) this.#isBlurEnabled.set(savedBlurEnabled === 'true');

    const savedBlurStrength = localStorage.getItem('selectedBlurStrength') as 'low' | 'medium' | 'high' | null;
    if (savedBlurStrength) this.#blurStrength.set(savedBlurStrength);

    effect(() => {
      const roster = this.#roster();
      if (roster.some((attendee) => attendee.externalUserId.startsWith('aws:MediaPipeline'))) {
        this.#isRecording.set(true);
      } else {
        this.#isRecording.set(false);
      }
    });

    effect(
      () => {
        const isPaneOpen = this.videoCallStateService.chatPaneState() === 'open';
        untracked(() => {
          this.isChatVisible.set(isPaneOpen);
        });
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const visible = this.isChatVisible();
        if (visible) {
          untracked(() => {
            this.#unreadCount.set(0);
          });
        }
      },
      { allowSignalWrites: true }
    );

    this.videoCallStateService.toggleDrawer$.pipe(takeUntilDestroyed()).subscribe((opened) => {
      if (opened) {
        this.listAndSelectInitialDevices();
      }
    });
  }

  addDeviceChangeObserver(): void {
    const observer: DeviceChangeObserver = {
      audioInputsChanged: (freshAudioInputDeviceList: MediaDeviceInfo[]) => {
        this.#audioInputDevices.set(freshAudioInputDeviceList);
      },
      videoInputsChanged: (freshVideoInputDeviceList: MediaDeviceInfo[]) => {
        this.#videoInputDevices.set(freshVideoInputDeviceList);
      },
      audioOutputsChanged: (freshAudioOutputDeviceList: MediaDeviceInfo[]) => {
        this.#audioOutputDevices.set(freshAudioOutputDeviceList);
      },
    };
    this.deviceController.addDeviceChangeObserver(observer);
  }

  async startVideoPreview(videoElement: HTMLVideoElement): Promise<void> {
    if (!this.#isCameraEnabled()) return;

    const cameraAccess = await firstValueFrom(this.permissionWatcher.cameraPermission$);
    if (cameraAccess === 'denied') {
      this.toast.showDevicePermissionError();
      this.setCameraEnabled(false);
      return;
    }

    const videoInputId = this.#selectedVideoInputId();
    if (!videoInputId) return;

    try {
      let device: any = videoInputId;
      if (this.#isBlurEnabled()) {
        try {
          const videoFxConfig: VideoFxConfig = {
            backgroundBlur: { isEnabled: true, strength: this.#blurStrength() },
            backgroundReplacement: { isEnabled: false, backgroundImageURL: null, defaultColor: undefined },
          };
          const processor = await VideoFxProcessor.create(this.logger, videoFxConfig);
          this.#videoFxProcessor.set(processor);
          const transformDevice = new DefaultVideoTransformDevice(this.logger, videoInputId, [processor]);
          this.#videoTransformDevice.set(transformDevice);
          device = transformDevice;
          this.#isVideoFxActive.set(true);
        } catch (error) {
          console.error('Failed to start video FX in preview', error);
        }
      }

      const stream = await this.deviceController.startVideoInput(device);
      if (stream) {
        videoElement.srcObject = stream;
        await videoElement.play();
        this.#previewVideoElement = videoElement;
        this.#videoPreviewIsActive.set(true);
      }
    } catch (err) {
      console.error('Failed to start video preview', err);
    }
  }

  async stopVideoPreview(): Promise<void> {
    try {
      await this.deviceController.stopVideoInput();
    } catch (e) {
      console.error('Failed to stop video input in preview', e);
    }
    if (this.#previewVideoElement && this.#previewVideoElement.srcObject) {
      const stream = this.#previewVideoElement.srcObject as MediaStream;
      stream.getTracks().forEach((track) => track.stop());
      this.#previewVideoElement.srcObject = null;
    }
    this.#previewVideoElement = null;
    this.#videoPreviewIsActive.set(false);

    // Clean up preview FX processor to avoid leaks
    if (this.#isVideoFxActive()) {
      this._cleanUpVideoFx();
    }
  }

  async setBlurPreference(enabled: boolean): Promise<void> {
    this.#isBlurEnabled.set(enabled);
    localStorage.setItem('selectedBlurEnabled', String(enabled));

    const session = this.#meetingSession();
    if (session) {
      if (this.#isCameraEnabled()) {
        if (enabled) {
          await this._applyBlurEffect();
        } else {
          await this._removeBlurEffect();
        }
      }
    } else if (this.#videoPreviewIsActive() && this.#previewVideoElement) {
      const element = this.#previewVideoElement;
      await this.stopVideoPreview();
      await this.startVideoPreview(element);
    }
  }

  async setBlurStrength(strength: 'low' | 'medium' | 'high'): Promise<void> {
    this.#blurStrength.set(strength);
    localStorage.setItem('selectedBlurStrength', strength);

    const session = this.#meetingSession();
    const processor = this.#videoFxProcessor();

    if (session && processor && this.#isBlurEnabled()) {
      const config: VideoFxConfig = {
        backgroundBlur: { isEnabled: true, strength: strength },
        backgroundReplacement: { isEnabled: false, backgroundImageURL: null, defaultColor: undefined },
      };
      await processor.setEffectConfig(config);
      console.log(`Updated active blur strength to: ${strength}`);
    } else if (!session && this.#videoPreviewIsActive() && this.#previewVideoElement) {
      const element = this.#previewVideoElement;
      await this.stopVideoPreview();
      await this.startVideoPreview(element);
    }
  }

  async joinMeetingWithAttendee(callID: string, username: string, attendee: Attendee): Promise<void> {
    await this.listAndSelectInitialDevices();
    const micAccess = await firstValueFrom(this.permissionWatcher.microphonePermission$);
    const cameraAccess = await firstValueFrom(this.permissionWatcher.cameraPermission$);

    if (micAccess === 'denied' || (this.#isCameraEnabled() && cameraAccess === 'denied')) {
      this.toast.showDevicePermissionError();
      throw new Error('Device permissions denied');
    }

    if (this.#videoPreviewIsActive()) {
      await this.stopVideoPreview();
    }
    try {
      const isContentEditor = this.contentStateService.isContentEditor();
      this.#isHost.set(isContentEditor);
      const meetingResponse = await firstValueFrom(
        this.chimeCallService.createChimeMeeting({
          callID,
          isAudioLocked: this.#isAudioLocked(),
          isCameraLocked: this.#isCameraLocked(),
          isScreenShareLocked: this.#isScreenShareLocked(),
          isChatLocked: this.#isChatLocked(),
          isWaitingRoomEnabled: this.#isWaitingRoomEnabled(),
          courseId: this.contentStateService.courseId,
          noteId: this.contentStateService.noteId,
          blogId: this.contentStateService.blogId,
        })
      );
      const meeting: Meeting = meetingResponse.Meeting;

      // Update local lock states from response
      this.#isAudioLocked.set(meetingResponse.isAudioLocked || false);
      this.#isCameraLocked.set(meetingResponse.isCameraLocked || false);
      this.#isScreenShareLocked.set(meetingResponse.isScreenShareLocked || false);
      this.#isChatLocked.set(meetingResponse.isChatLocked || false);
      this.#isWaitingRoomEnabled.set(meetingResponse.isWaitingRoomEnabled || false);

      if (meetingResponse.chatMessages) {
        this.#chatMessages.set(meetingResponse.chatMessages);
      }

      if (isContentEditor && attendee.AttendeeId) {
        this.#hostAttendeeIds.update((hosts) => new Set([...hosts, attendee.AttendeeId!]));
      }
      const configuration = new MeetingSessionConfiguration(meeting, attendee);
      const session = new DefaultMeetingSession(configuration, this.logger, this.deviceController);
      this.#meetingSession.set(session);
      this.applyAudioProfile();
      this.applyVideoQuality();
      this.#setupObservers();

      // 1. Select and start audio input
      const audioInputId = this.#selectedAudioInputId();
      if (audioInputId) {
        const constraints = this.getAudioConstraints(audioInputId);
        await session.audioVideo.startAudioInput(constraints);
      }

      // 2. Select and start video input (with blur if requested)
      const videoInputId = this.#selectedVideoInputId();
      if (videoInputId && this.#isCameraEnabled()) {
        if (this.#isBlurEnabled()) {
          try {
            const videoFxConfig: VideoFxConfig = {
              backgroundBlur: { isEnabled: true, strength: this.#blurStrength() },
              backgroundReplacement: { isEnabled: false, backgroundImageURL: null, defaultColor: undefined },
            };
            const processor = await VideoFxProcessor.create(this.logger, videoFxConfig);
            this.#videoFxProcessor.set(processor);
            const transformDevice = new DefaultVideoTransformDevice(this.logger, videoInputId, [processor]);
            this.#videoTransformDevice.set(transformDevice);
            await session.audioVideo.startVideoInput(transformDevice);
            this.#isVideoFxActive.set(true);
          } catch (error) {
            console.error('Failed to start video FX on join, falling back to normal video.', error);
            await session.audioVideo.startVideoInput(videoInputId);
          }
        } else {
          await session.audioVideo.startVideoInput(videoInputId);
        }
      }

      // 3. Select audio output and bind the required audio element
      const audioOutputId = this.#selectedAudioOutputId();
      if (audioOutputId) {
        await session.audioVideo.chooseAudioOutput(audioOutputId);
        this.#meetingAudioElement = document.createElement('audio');
        this.#meetingAudioElement.style.display = 'none';
        document.body.appendChild(this.#meetingAudioElement);
        await session.audioVideo.bindAudioElement(this.#meetingAudioElement);
      }

      // 4. Start the session itself
      await session.audioVideo.start();
      this.videoCallStateService.preventDrawerClose.set(true);
      this.videoCallStateService.activeCallId.set(callID);

      // Start meeting transcription
      // this.startMeetingTranscription(callID, meetingResponse.geminiInteraction);

      // 5. Start the local video tile if the camera is on
      if (this.#isCameraEnabled()) {
        session.audioVideo.startLocalVideoTile();
      }

      // 6. Apply the initial mute state now that the session is active
      if (this.#isMuted()) {
        session.audioVideo.realtimeMuteLocalAudio();
      }

      // 7. Start the active speaker detection
      this.#startActiveSpeakerDetector();

      // 8. Start meeting duration timer
      this.startDurationTimer(meetingResponse.startedAt);
    } catch (error) {
      console.error('Failed to join meeting with attendee credentials:', error);
      this.#cleanUp();
      throw error;
    }
  }

  async joinMeeting(callID: string, username: string, isHost: boolean = false): Promise<void> {
    try {
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
    } catch (e) {
      console.warn('Could not initialize audio context:', e);
    }

    await this.listAndSelectInitialDevices();
    const micAccess = await firstValueFrom(this.permissionWatcher.microphonePermission$);
    const cameraAccess = await firstValueFrom(this.permissionWatcher.cameraPermission$);

    if (micAccess === 'denied' || (this.#isCameraEnabled() && cameraAccess === 'denied')) {
      this.toast.showDevicePermissionError();
      throw new Error('Device permissions denied');
    }

    if (this.#videoPreviewIsActive()) {
      await this.stopVideoPreview();
    }
    try {
      const isContentEditor = this.contentStateService.isContentEditor();
      this.#isHost.set(isContentEditor);
      const meetingResponse = await firstValueFrom(
        this.chimeCallService.createChimeMeeting({
          callID,
          isAudioLocked: this.#isAudioLocked(),
          isCameraLocked: this.#isCameraLocked(),
          isScreenShareLocked: this.#isScreenShareLocked(),
          isChatLocked: this.#isChatLocked(),
          courseId: this.contentStateService.courseId,
          noteId: this.contentStateService.noteId,
          blogId: this.contentStateService.blogId,
        })
      );
      const meeting: Meeting = meetingResponse.Meeting;

      // Update local lock states from response
      this.#isAudioLocked.set(meetingResponse.isAudioLocked || false);
      this.#isCameraLocked.set(meetingResponse.isCameraLocked || false);
      this.#isScreenShareLocked.set(meetingResponse.isScreenShareLocked || false);
      this.#isChatLocked.set(meetingResponse.isChatLocked || false);

      if (meetingResponse.chatMessages) {
        this.#chatMessages.set(meetingResponse.chatMessages);
      }
      const userId = this.authService.getID() || this.auth.currentUser?.uid || 'anonymous-guest';
      const attendeeResponse = await firstValueFrom(this.chimeCallService.joinChimeMeeting(meeting.MeetingId!, userId, username));
      const attendee: Attendee = attendeeResponse.Attendee;
      if (isHost && attendee.AttendeeId) {
        this.#hostAttendeeIds.update((hosts) => new Set([...hosts, attendee.AttendeeId!]));
      }
      const configuration = new MeetingSessionConfiguration(meeting, attendee);
      const session = new DefaultMeetingSession(configuration, this.logger, this.deviceController);
      this.#meetingSession.set(session);
      this.applyAudioProfile();
      this.applyVideoQuality();
      this.#setupObservers();

      // Sequentially select and start devices BEFORE starting the session.
      // This prevents race conditions caused by setTimeout.

      // 1. Select and start audio input
      const audioInputId = this.#selectedAudioInputId();
      if (audioInputId) {
        const constraints = this.getAudioConstraints(audioInputId);
        await session.audioVideo.startAudioInput(constraints);
      }

      // 2. Select and start video input (with blur if requested)
      const videoInputId = this.#selectedVideoInputId();
      if (videoInputId && this.#isCameraEnabled()) {
        if (this.#isBlurEnabled()) {
          try {
            const videoFxConfig: VideoFxConfig = {
              backgroundBlur: { isEnabled: true, strength: this.#blurStrength() },
              backgroundReplacement: { isEnabled: false, backgroundImageURL: null, defaultColor: undefined },
            };
            const processor = await VideoFxProcessor.create(this.logger, videoFxConfig);
            this.#videoFxProcessor.set(processor);
            const transformDevice = new DefaultVideoTransformDevice(this.logger, videoInputId, [processor]);
            this.#videoTransformDevice.set(transformDevice);
            await session.audioVideo.startVideoInput(transformDevice);
            this.#isVideoFxActive.set(true);
          } catch (error) {
            console.error('Failed to start video FX on join, falling back to normal video.', error);
            await session.audioVideo.startVideoInput(videoInputId);
          }
        } else {
          await session.audioVideo.startVideoInput(videoInputId);
        }
      }

      // 3. Select audio output and bind the required audio element
      const audioOutputId = this.#selectedAudioOutputId();
      if (audioOutputId) {
        await session.audioVideo.chooseAudioOutput(audioOutputId);
        this.#meetingAudioElement = document.createElement('audio');
        this.#meetingAudioElement.style.display = 'none';
        document.body.appendChild(this.#meetingAudioElement);
        await session.audioVideo.bindAudioElement(this.#meetingAudioElement);
      }

      // 4. Start the session itself
      await session.audioVideo.start();
      this.videoCallStateService.preventDrawerClose.set(true);
      this.videoCallStateService.activeCallId.set(callID);

      // Start meeting transcription
      // this.startMeetingTranscription(callID, meetingResponse.geminiInteraction);

      // 5. Start the local video tile if the camera is on
      if (this.#isCameraEnabled()) {
        session.audioVideo.startLocalVideoTile();
      }

      // 6. Apply the initial mute state now that the session is active
      if (this.#isMuted()) {
        session.audioVideo.realtimeMuteLocalAudio();
      }

      // 7. Start the active speaker detection
      this.#startActiveSpeakerDetector();

      // 8. Start meeting duration timer
      this.startDurationTimer(meetingResponse.startedAt);
    } catch (error) {
      console.error('Failed to join Chime meeting:', error);
      this.#cleanUp();
      throw error;
    }
  }

  async leaveMeeting(): Promise<void> {
    this.stopMeetingTranscription();
    this.stopGeminiLiveAssistant();
    const session = this.#meetingSession();
    if (!session) {
      this.#cleanUp();
      return;
    }
    try {
      await session.audioVideo.stopVideoInput();
      await session.audioVideo.stopAudioInput();
      await session.audioVideo.stop();
    } catch (err) {
      console.error('Error leaving Chime meeting:', err);
    } finally {
      this.#cleanUp();
    }
  }

  setCameraEnabled(enabled: boolean): void {
    this.#isCameraEnabled.set(enabled);
  }

  async toggleCamera(): Promise<void> {
    const session = this.#meetingSession();
    if (!session) return;
    if (this.#isCameraEnabled()) {
      await session.audioVideo.stopLocalVideoTile();
      await session.audioVideo.stopVideoInput();
      // Clean up the FX processor when the camera is turned off
      this._cleanUpVideoFx();
      this.#isCameraEnabled.set(false);
    } else {
      if (this.isCameraLocked() && !this.#isHost()) {
        this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.CameraLocked') });
        return;
      }
      const cameraAccess = await firstValueFrom(this.permissionWatcher.cameraPermission$);
      if (cameraAccess === 'denied') {
        this.toast.showDevicePermissionError();
        return;
      }
      const videoInputId = this.#selectedVideoInputId();
      if (videoInputId) {
        if (this.#isBlurEnabled()) {
          await this._applyBlurEffect();
        } else {
          await session.audioVideo.startVideoInput(videoInputId);
        }
        session.audioVideo.startLocalVideoTile();
        this.#isCameraEnabled.set(true);
      }
    }
  }

  async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    this.#isMuted.set(!enabled);
    if (enabled) {
      const micAccess = await firstValueFrom(this.permissionWatcher.microphonePermission$);
      if (micAccess === 'denied') {
        this.toast.showDevicePermissionError();
        this.#isMuted.set(true);
        return;
      }
    }
  }

  async toggleMicrophone(): Promise<void> {
    const session = this.#meetingSession();
    if (!session) return;
    if (this.#isMuted()) {
      if (this.isAudioLocked() && !this.#isHost()) {
        this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.AudioLocked') });
        return;
      }
      const micAccess = await firstValueFrom(this.permissionWatcher.microphonePermission$);
      if (micAccess === 'denied') {
        this.toast.showDevicePermissionError();
        return;
      }
      session.audioVideo.realtimeUnmuteLocalAudio();
      this.#isMuted.set(false);
    } else {
      session.audioVideo.realtimeMuteLocalAudio();
      this.#isMuted.set(true);
    }
  }

  public sendMessage(message: string): void {
    const session = this.#meetingSession();
    if (!session) return;
    session.audioVideo.realtimeSendDataMessage(CHAT_DATA_MESSAGE_TOPIC, message, 1000);
    const localAttendeeId = session.configuration.credentials?.attendeeId;
    const roster = this.#roster();
    const localUser = roster.find((a) => a.chimeAttendeeId === localAttendeeId);
    const senderId = localUser?.externalUserId || this.authService.getID();
    const senderName = this.authService.userInfo()?.fullName || 'Anonymous';
    const timestamp = Date.now();

    this.#chatMessages.update((messages) => [...messages, { timestamp, senderName: 'You', senderId, message }]);

    const callID = this.videoCallStateService.activeCallId();
    if (callID) {
      this.chimeCallService.saveChatMessage(callID, { senderId, senderName, message, timestamp }).subscribe({
        error: (err) => console.error('Failed to save chat message to DB:', err),
      });
    }
  }

  async startScreenShare(mode: 'content-only' | 'with-sidebar' | 'full' = 'content-only'): Promise<void> {
    const session = this.#meetingSession();
    if (session) {
      if (this.isScreenShareLocked() && !this.#isHost()) {
        this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.ScreenShareLocked') });
        return;
      }
      try {
        // 1. Prompt for screensharing. Set options based on the chosen mode.
        const isCropped = mode === 'content-only' || mode === 'with-sidebar';
        const options: any = {
          video: isCropped ? { displaySurface: 'browser' } : true,
        };
        if (isCropped) {
          options.selfBrowserSurface = 'include';
          options.preferCurrentTab = true;
          options.monitorTypeSurfaces = 'exclude'; // Hides the "Entire Screen" tab
        }

        const stream = await navigator.mediaDevices.getDisplayMedia(options);

        // 2. Apply CropTarget if Region Capture is supported
        if (isCropped) {
          const [videoTrack] = stream.getVideoTracks();
          if (videoTrack && 'cropTo' in videoTrack) {
            try {
              const selector = mode === 'content-only' ? '.content-card-body' : '.screenshare-crop-helper';
              const element = document.querySelector(selector);
              if (element) {
                const CropTarget = (window as any).CropTarget;
                if (CropTarget && typeof CropTarget.fromElement === 'function') {
                  const cropTarget = await CropTarget.fromElement(element);
                  await (videoTrack as any).cropTo(cropTarget);
                  console.log(`Successfully cropped screenshare stream to ${selector} element`);
                }
              }
            } catch (cropErr) {
              console.warn('Failed to crop video track to target element, sharing full tab instead:', cropErr);
            }
          }
        }

        // 3. Start sharing the custom MediaStream using startContentShare
        await session.audioVideo.startContentShare(stream);
      } catch (err: any) {
        console.error('Screen share failed to start:', err);
      }
    }
  }

  async stopScreenShare(): Promise<void> {
    const session = this.#meetingSession();
    if (session) {
      await session.audioVideo.stopContentShare();
    }
  }

  async toggleBackgroundBlur(): Promise<void> {
    await this.setBlurPreference(!this.#isBlurEnabled());
  }

  async startRecording(callId: string): Promise<void> {
    const session = this.#meetingSession();
    if (!session) return;
    const meetingId = session.configuration.meetingId;
    if (!meetingId) return;
    await firstValueFrom(this.chimeCallService.startRecording(meetingId, callId));
    this.#isRecording.set(true);
  }

  async stopRecording(callId: string): Promise<void> {
    const session = this.#meetingSession();
    if (!session) return;
    await firstValueFrom(this.chimeCallService.stopRecording(callId));
    this.#isRecording.set(false);
  }

  async startPstnCall(phoneNumber: string, callId: string): Promise<string | null> {
    const session = this.#meetingSession();
    if (!session) return null;

    const meetingId = session.configuration.meetingId;
    const attendeeId = session.configuration.credentials?.attendeeId;

    if (!meetingId || !attendeeId) return null;

    this.#isDialing.set(true);
    try {
      const response = await firstValueFrom(this.chimeCallService.startPstnCall(meetingId, attendeeId, phoneNumber, callId));
      return response.transactionId;
    } catch (error) {
      console.error('Failed to start PSTN call:', error);
      return null;
    } finally {
      this.#isDialing.set(false);
    }
  }

  public muteParticipant(attendeeId: string): void {
    const session = this.#meetingSession();
    if (!session || !this.#isHost()) return;
    const muteAction = { action: 'mute', targetAttendeeId: attendeeId };
    session.audioVideo.realtimeSendDataMessage('moderation', muteAction, 1000);
  }

  public stopVideoParticipant(attendeeId: string): void {
    const session = this.#meetingSession();
    if (!session || !this.#isHost()) return;
    const stopVideoAction = { action: 'stop-video', targetAttendeeId: attendeeId };
    session.audioVideo.realtimeSendDataMessage('moderation', stopVideoAction, 1000);
  }

  public muteAllParticipants(): void {
    const session = this.#meetingSession();
    if (!session || !this.#isHost()) return;
    const muteAllAction = { action: 'mute-all' };
    session.audioVideo.realtimeSendDataMessage('moderation', muteAllAction, 1000);
  }

  public kickParticipant(attendeeId: string): void {
    const session = this.#meetingSession();
    if (!session || !this.#isHost()) return;
    const callID = this.videoCallStateService.activeCallId();
    if (!callID) return;

    // Send local real-time broadcast message for immediate client updates
    const kickAction = { action: 'kick', targetAttendeeId: attendeeId };
    session.audioVideo.realtimeSendDataMessage('moderation', kickAction, 1000);

    // Eject attendee via Chime backend SDK (ensures PSTN phone lines are hung up)
    this.chimeCallService.kickParticipant(callID, attendeeId).subscribe({
      next: () => {
        console.log(`Successfully kicked participant ${attendeeId} via backend`);
      },
      error: (err) => {
        console.error(`Failed to kick participant ${attendeeId} via backend:`, err);
      },
    });
  }

  public toggleLockAudio(): void {
    const callID = this.videoCallStateService.activeCallId();
    if (!callID) return;
    this.updateMeetingSettings(callID, { isAudioLocked: !this.#isAudioLocked() });
  }

  public toggleLockCamera(): void {
    const callID = this.videoCallStateService.activeCallId();
    if (!callID) return;
    this.updateMeetingSettings(callID, { isCameraLocked: !this.#isCameraLocked() });
  }

  public toggleLockScreenShare(): void {
    const callID = this.videoCallStateService.activeCallId();
    if (!callID) return;
    this.updateMeetingSettings(callID, { isScreenShareLocked: !this.#isScreenShareLocked() });
  }

  public toggleLockChat(): void {
    const callID = this.videoCallStateService.activeCallId();
    if (!callID) return;
    this.updateMeetingSettings(callID, { isChatLocked: !this.#isChatLocked() });
  }

  public promoteParticipant(attendeeId: string): void {
    const session = this.#meetingSession();
    if (!session || !this.#isHost()) return;
    this.#hostAttendeeIds.update((hosts) => new Set([...hosts, attendeeId]));
    const promoteAction = { action: 'promote-host', targetAttendeeId: attendeeId };
    session.audioVideo.realtimeSendDataMessage('moderation', promoteAction, 1000);
  }

  async fetchMeetingSettings(callID: string): Promise<void> {
    try {
      const settings = await firstValueFrom(this.chimeCallService.getCallSettings(callID));
      this.#isAudioLocked.set(settings.isAudioLocked || false);
      this.#isCameraLocked.set(settings.isCameraLocked || false);
      this.#isScreenShareLocked.set(settings.isScreenShareLocked || false);
      this.#isChatLocked.set(settings.isChatLocked || false);
      this.#isWaitingRoomEnabled.set(settings.isWaitingRoomEnabled || false);
      if ((settings as any).tenantId) {
        this.#tenantId.set((settings as any).tenantId);
      }

      const currentUserId = this.authService.getID() || this.auth.currentUser?.uid;
      const isCreator = !!(settings.createdBy && currentUserId && settings.createdBy === currentUserId);
      const isContentEditor = this.contentStateService.isContentEditor();
      this.#isHost.set(isContentEditor || isCreator);
    } catch (error) {
      console.error('Failed to fetch meeting settings:', error);
    }
  }

  async updateMeetingSettings(
    callID: string,
    settings: { isAudioLocked?: boolean; isCameraLocked?: boolean; isScreenShareLocked?: boolean; isChatLocked?: boolean; isWaitingRoomEnabled?: boolean }
  ): Promise<void> {
    const session = this.#meetingSession();

    if (settings.isAudioLocked !== undefined) this.#isAudioLocked.set(settings.isAudioLocked);
    if (settings.isCameraLocked !== undefined) this.#isCameraLocked.set(settings.isCameraLocked);
    if (settings.isScreenShareLocked !== undefined) this.#isScreenShareLocked.set(settings.isScreenShareLocked);
    if (settings.isChatLocked !== undefined) this.#isChatLocked.set(settings.isChatLocked);
    if (settings.isWaitingRoomEnabled !== undefined) this.#isWaitingRoomEnabled.set(settings.isWaitingRoomEnabled);

    try {
      await firstValueFrom(this.chimeCallService.saveCallSettings(callID, settings));
    } catch (error) {
      console.error('Failed to save call settings:', error);
    }

    if (session && this.#isHost()) {
      if (settings.isAudioLocked !== undefined) {
        session.audioVideo.realtimeSendDataMessage('moderation', { action: 'lock-audio', locked: settings.isAudioLocked }, 1000);
      }
      if (settings.isCameraLocked !== undefined) {
        session.audioVideo.realtimeSendDataMessage('moderation', { action: 'lock-camera', locked: settings.isCameraLocked }, 1000);
      }
      if (settings.isScreenShareLocked !== undefined) {
        session.audioVideo.realtimeSendDataMessage('moderation', { action: 'lock-screen-share', locked: settings.isScreenShareLocked }, 1000);
      }
      if (settings.isChatLocked !== undefined) {
        session.audioVideo.realtimeSendDataMessage('moderation', { action: 'lock-chat', locked: settings.isChatLocked }, 1000);
      }
    }
  }

  async listAndSelectInitialDevices(): Promise<void> {
    const videoInputs = await this.deviceController.listVideoInputDevices();
    const audioInputs = await this.deviceController.listAudioInputDevices();
    const audioOutputs = await this.deviceController.listAudioOutputDevices();

    this.#videoInputDevices.set(videoInputs);
    this.#audioInputDevices.set(audioInputs);
    this.#audioOutputDevices.set(audioOutputs);

    const savedVideoId = localStorage.getItem('selectedCamera');
    const savedAudioInputId = localStorage.getItem('selectedMicrophone');
    const savedAudioOutputId = localStorage.getItem('selectedSpeaker');

    const initialVideoId = videoInputs.find((d) => d.deviceId === savedVideoId)?.deviceId || (videoInputs.length > 0 ? videoInputs[0].deviceId : null);
    const initialAudioInputId = audioInputs.find((d) => d.deviceId === savedAudioInputId)?.deviceId || (audioInputs.length > 0 ? audioInputs[0].deviceId : null);
    const initialAudioOutputId = audioOutputs.find((d) => d.deviceId === savedAudioOutputId)?.deviceId || (audioOutputs.length > 0 ? audioOutputs[0].deviceId : null);

    this.#selectedVideoInputId.set(initialVideoId);
    this.#selectedAudioInputId.set(initialAudioInputId);
    this.#selectedAudioOutputId.set(initialAudioOutputId);
  }

  async selectVideoInput(deviceId: string): Promise<void> {
    this.#selectedVideoInputId.set(deviceId);
    localStorage.setItem('selectedCamera', deviceId);

    const session = this.#meetingSession();
    if (session) {
      this.applyVideoQuality();
      await session.audioVideo.startVideoInput(deviceId);
      // If camera is already on, restart it with the new device
      if (this.isCameraEnabled()) {
        await session.audioVideo.stopLocalVideoTile();
        session.audioVideo.startLocalVideoTile();
      }
    } else if (this.videoPreviewIsActive() && this.#previewVideoElement) {
      const element = this.#previewVideoElement;
      await this.stopVideoPreview();
      await this.startVideoPreview(element);
    }
  }

  async selectAudioInput(deviceId: string): Promise<void> {
    this.#selectedAudioInputId.set(deviceId);
    localStorage.setItem('selectedMicrophone', deviceId);

    const session = this.#meetingSession();
    if (session) {
      const constraints = this.getAudioConstraints(deviceId);
      await session.audioVideo.startAudioInput(constraints);
    }
  }

  async selectAudioOutput(deviceId: string): Promise<void> {
    this.#selectedAudioOutputId.set(deviceId);
    localStorage.setItem('selectedSpeaker', deviceId);

    const session = this.#meetingSession();
    if (session) {
      await session.audioVideo.chooseAudioOutput(deviceId);
    }
  }

  getAudioConstraints(deviceId: string): MediaTrackConstraints {
    return {
      deviceId: { exact: deviceId },
      echoCancellation: this.#echoCancellation(),
      noiseSuppression: this.#noiseSuppression(),
      autoGainControl: this.#autoGainControl(),
    };
  }

  applyAudioProfile(): void {
    const session = this.#meetingSession();
    if (!session) return;

    const quality = this.#audioQuality();
    let profile: AudioProfile;
    if (quality === 'high-speech') {
      profile = AudioProfile.fullbandSpeechMono();
    } else if (quality === 'high-music') {
      profile = AudioProfile.fullbandMusicMono();
    } else {
      profile = new AudioProfile();
    }
    session.audioVideo.setAudioProfile(profile);
    console.log(`Applied AudioProfile for quality: ${quality}`);
  }

  async setAudioQuality(quality: 'standard' | 'high-speech' | 'high-music'): Promise<void> {
    this.#audioQuality.set(quality);
    localStorage.setItem('selectedAudioQuality', quality);
    this.applyAudioProfile();
  }

  applyVideoQuality(): void {
    const session = this.#meetingSession();
    if (!session) return;

    const quality = this.#videoQuality();
    if (quality === 'hd') {
      session.audioVideo.chooseVideoInputQuality(1280, 720, 30);
      session.audioVideo.setVideoMaxBandwidthKbps(1400);
    } else if (quality === 'fhd') {
      session.audioVideo.chooseVideoInputQuality(1920, 1080, 30);
      session.audioVideo.setVideoMaxBandwidthKbps(2500);
    } else {
      session.audioVideo.chooseVideoInputQuality(960, 540, 15);
      session.audioVideo.setVideoMaxBandwidthKbps(512);
    }
    console.log(`Applied VideoInputQuality for quality: ${quality}`);
  }

  async setVideoQuality(quality: 'standard' | 'hd' | 'fhd'): Promise<void> {
    this.#videoQuality.set(quality);
    localStorage.setItem('selectedVideoQuality', quality);

    const session = this.#meetingSession();
    if (session) {
      this.applyVideoQuality();
      if (this.#isCameraEnabled()) {
        const videoInputId = this.#selectedVideoInputId();
        if (videoInputId) {
          await session.audioVideo.stopLocalVideoTile();
          if (this.#isBlurEnabled()) {
            await this._applyBlurEffect();
          } else {
            await session.audioVideo.startVideoInput(videoInputId);
          }
          session.audioVideo.startLocalVideoTile();
        }
      }
    }
  }

  async setEchoCancellation(enabled: boolean): Promise<void> {
    this.#echoCancellation.set(enabled);
    localStorage.setItem('echoCancellation', String(enabled));
    await this.reapplyAudioConstraints();
  }

  async setNoiseSuppression(enabled: boolean): Promise<void> {
    this.#noiseSuppression.set(enabled);
    localStorage.setItem('noiseSuppression', String(enabled));
    await this.reapplyAudioConstraints();
  }

  async setAutoGainControl(enabled: boolean): Promise<void> {
    this.#autoGainControl.set(enabled);
    localStorage.setItem('autoGainControl', String(enabled));
    await this.reapplyAudioConstraints();
  }

  private async reapplyAudioConstraints(): Promise<void> {
    const session = this.#meetingSession();
    const audioInputId = this.#selectedAudioInputId();
    if (session && audioInputId) {
      const constraints = this.getAudioConstraints(audioInputId);
      await session.audioVideo.startAudioInput(constraints);
    }
  }

  private async _applyBlurEffect(): Promise<void> {
    const session = this.#meetingSession();
    const videoInputId = this.#selectedVideoInputId();
    if (!session || !videoInputId) {
      console.error('Cannot apply video FX: no session or video input selected.');
      return;
    }

    // If FX are already active, no need to re-apply
    if (this.#isVideoFxActive()) return;

    try {
      const videoFxConfig: VideoFxConfig = {
        backgroundBlur: { isEnabled: true, strength: this.#blurStrength() },
        backgroundReplacement: { isEnabled: false, backgroundImageURL: null, defaultColor: undefined },
      };
      const processor = await VideoFxProcessor.create(this.logger, videoFxConfig);
      this.#videoFxProcessor.set(processor);
      const transformDevice = new DefaultVideoTransformDevice(this.logger, videoInputId, [processor]);
      this.#videoTransformDevice.set(transformDevice);
      await session.audioVideo.startVideoInput(transformDevice);
      this.#isVideoFxActive.set(true);
    } catch (error) {
      console.error('Failed to start video FX:', error);
      this._cleanUpVideoFx();
      // Fallback to normal video
      await session.audioVideo.startVideoInput(videoInputId);
    }
  }

  private async _removeBlurEffect(): Promise<void> {
    const session = this.#meetingSession();
    if (!session) return;

    // If FX are not active, nothing to remove
    if (!this.#isVideoFxActive()) return;

    this._cleanUpVideoFx();

    // Revert to original device
    const videoInputId = this.#selectedVideoInputId();
    if (videoInputId) {
      await session.audioVideo.startVideoInput(videoInputId);
    }
  }

  private _cleanUpVideoFx(): void {
    this.#videoTransformDevice()?.stop();
    this.#videoTransformDevice.set(null);
    this.#videoFxProcessor()?.destroy();
    this.#videoFxProcessor.set(null);
    this.#isVideoFxActive.set(false);
  }

  #setupObservers(): void {
    const session = this.#meetingSession();
    if (!session) return;

    const observer = {
      videoTileDidUpdate: (tileState: VideoTileState) => {
        if (!tileState.tileId) return;
        if (tileState.isContent) {
          this.#screenShareTile.set(tileState);
          return;
        }
        if (tileState.localTile) this.#localTileId.set(tileState.tileId);
        this.#remoteTiles.update((tiles) =>
          tiles.some((t) => t.tileId === tileState.tileId) ? tiles.map((t) => (t.tileId === tileState.tileId ? tileState : t)) : [...tiles, tileState]
        );
      },
      videoTileWasRemoved: (tileId: number) => {
        if (this.#screenShareTile()?.tileId === tileId) {
          this.#screenShareTile.set(null);
          return;
        }
        if (this.#localTileId() === tileId) this.#localTileId.set(null);
        this.#remoteTiles.update((tiles) => tiles.filter((t) => t.tileId !== tileId));
      },
      audioVideoDidStop: () => {
        // Explicitly stop all media tracks to release camera/microphone resources.
        // This is crucial for remote participants when the host ends the meeting.
        this.deviceController.stopVideoInput();
        this.deviceController.stopAudioInput();
        this.#cleanUp();
      },
      attendeesDidMute: (attendeeIds: string[]) => {
        this.#updateMuteStatus(attendeeIds, true);
      },
      attendeesDidUnmute: (attendeeIds: string[]) => {
        this.#updateMuteStatus(attendeeIds, false);
      },
    };
    session.audioVideo.addObserver(observer);

    session.audioVideo.realtimeSubscribeToReceiveDataMessage(CHAT_DATA_MESSAGE_TOPIC, (dataMessage: DataMessage) => {
      const localAttendeeId = session.configuration.credentials?.attendeeId;
      if (dataMessage.senderAttendeeId === localAttendeeId) {
        return;
      }
      const sender = this.#roster().find((a) => a.chimeAttendeeId === dataMessage.senderAttendeeId);
      const message = new TextDecoder().decode(dataMessage.data);
      this.#chatMessages.update((messages) => [
        ...messages,
        { timestamp: dataMessage.timestampMs, senderName: sender?.username || 'Anonymous', senderId: sender?.externalUserId || '', message },
      ]);
      if (!this.isChatVisible()) {
        this.#unreadCount.update((count) => count + 1);
      }
    });

    session.audioVideo.realtimeSubscribeToReceiveDataMessage('moderation', (dataMessage: DataMessage) => {
      const message = JSON.parse(new TextDecoder().decode(dataMessage.data));
      const localAttendeeId = session.configuration.credentials?.attendeeId;
      if (message.action === 'mute' && message.targetAttendeeId === localAttendeeId) {
        if (!this.#isMuted()) {
          this.toggleMicrophone(); // Mute the local user
        }
      }
      if (message.action === 'mute-all') {
        const isSender = dataMessage.senderAttendeeId === localAttendeeId;
        if (!isSender && !this.#isMuted()) {
          this.toggleMicrophone();
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.MutedByHost') });
        }
      }
      if (message.action === 'stop-video' && message.targetAttendeeId === localAttendeeId) {
        if (this.#isCameraEnabled()) {
          this.toggleCamera(); // Turn off local video
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.VideoStoppedByHost') });
        }
      }
      if (message.action === 'kick' && message.targetAttendeeId === localAttendeeId) {
        this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.KickedByHost') });
        this.leaveMeeting();
      }
      if (message.action === 'lock-audio') {
        const nextState = !!message.locked;
        this.#isAudioLocked.set(nextState);
        if (nextState && !this.#isHost() && !this.#isMuted()) {
          this.toggleMicrophone(); // Force mute
          this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.AudioLockedByHost') });
        } else if (!nextState && !this.#isHost()) {
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.AudioUnlockedByHost') });
        }
      }
      if (message.action === 'lock-camera') {
        const nextState = !!message.locked;
        this.#isCameraLocked.set(nextState);
        if (nextState && !this.#isHost() && this.#isCameraEnabled()) {
          this.toggleCamera(); // Force stop camera
          this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.CameraLockedByHost') });
        } else if (!nextState && !this.#isHost()) {
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.CameraUnlockedByHost') });
        }
      }
      if (message.action === 'lock-screen-share') {
        const nextState = !!message.locked;
        this.#isScreenShareLocked.set(nextState);
        if (nextState && !this.#isHost() && this.isScreenSharing()) {
          this.stopScreenShare(); // Force stop screen sharing
          this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.ScreenShareLockedByHost') });
        } else if (!nextState && !this.#isHost()) {
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.ScreenShareUnlockedByHost') });
        }
      }
      if (message.action === 'lock-chat') {
        const nextState = !!message.locked;
        this.#isChatLocked.set(nextState);
        if (nextState && !this.#isHost()) {
          this.toast.showWithoutHeader({ level: 'error', text: this.translate.instant('VideoCall.Toast.ChatLockedByHost') });
          this.closeChatPane();
        } else if (!nextState && !this.#isHost()) {
          this.toast.showWithoutHeader({ level: 'info', text: this.translate.instant('VideoCall.Toast.ChatUnlockedByHost') });
        }
      }
      if (message.action === 'promote-host') {
        const targetAttendeeId = message.targetAttendeeId;
        this.#hostAttendeeIds.update((hosts) => new Set([...hosts, targetAttendeeId]));
        if (targetAttendeeId === session.configuration.credentials?.attendeeId) {
          this.#isHost.set(true);
          this.toast.showWithoutHeader({ level: 'success', text: this.translate.instant('VideoCall.Toast.PromotedToHost') });
        }
      }
      if (message.action === 'toggle-whiteboard') {
        const opened = !!message.opened;
        if (opened) {
          this.videoCallStateService.openWhiteboard();
        } else {
          this.videoCallStateService.closeWhiteboard();
        }
      }
      if (message.action === 'sync-moderation-state') {
        const nextAudioState = !!message.isAudioLocked;
        const nextCameraState = !!message.isCameraLocked;
        const nextScreenShareState = !!message.isScreenShareLocked;
        const nextChatState = !!message.isChatLocked;
        const nextWhiteboardState = !!message.isWhiteboardOpened;
        const hostIds = message.hostAttendeeIds || [];
        this.#isAudioLocked.set(nextAudioState);
        this.#isCameraLocked.set(nextCameraState);
        this.#isScreenShareLocked.set(nextScreenShareState);
        this.#isChatLocked.set(nextChatState);
        this.#hostAttendeeIds.set(new Set(hostIds));

        const localAttendeeId = session.configuration.credentials?.attendeeId;
        if (localAttendeeId && hostIds.includes(localAttendeeId)) {
          this.#isHost.set(true);
        }

        if (!this.#isHost()) {
          if (nextAudioState && !this.#isMuted()) {
            this.toggleMicrophone();
          }
          if (nextCameraState && this.#isCameraEnabled()) {
            this.toggleCamera();
          }
          if (nextScreenShareState && this.isScreenSharing()) {
            this.stopScreenShare();
          }
          if (nextChatState) {
            this.closeChatPane();
          }
          if (nextWhiteboardState) {
            this.videoCallStateService.openWhiteboard();
          } else {
            this.videoCallStateService.closeWhiteboard();
          }
        }
      }
    });

    session.audioVideo.realtimeSubscribeToAttendeeIdPresence((attendeeId, present, externalUserId) => {
      console.log(`Attendee ID presence changed: ${attendeeId}, present: ${present}, externalUserId: ${externalUserId}`);

      if (!externalUserId || attendeeId.endsWith('#content')) {
        return;
      }
      const [userId, username, type] = (externalUserId || '#').split('#');
      const isPstn = type === 'pstn';
      if (present) {
        let isNew = false;
        this.#roster.update((roster) => {
          if (roster.some((a) => a.chimeAttendeeId === attendeeId)) return roster;
          isNew = true;
          return [...roster, { chimeAttendeeId: attendeeId, externalUserId: userId, username: username || 'Anonymous', isMuted: false, isPstn }];
        });

        if (isNew && this.#isHost() && attendeeId !== session.configuration.credentials?.attendeeId) {
          // Sound removed from here; only played for waiting room
        }

        // If Host, sync late joiner lock states immediately
        if (this.#isHost()) {
          const syncAction = {
            action: 'sync-moderation-state',
            isAudioLocked: this.#isAudioLocked(),
            isCameraLocked: this.#isCameraLocked(),
            isScreenShareLocked: this.#isScreenShareLocked(),
            isChatLocked: this.#isChatLocked(),
            isWhiteboardOpened: this.videoCallStateService.whiteboardOpened(),
            hostAttendeeIds: Array.from(this.#hostAttendeeIds()),
          };
          session.audioVideo.realtimeSendDataMessage('moderation', syncAction, 1000);
        }

        session.audioVideo.realtimeSubscribeToVolumeIndicator(attendeeId, (attendeeId, volume, muted, signalStrength) => {
          this.#speakerVolumes.update((volumes) => {
            const newVolumes = new Map(volumes);
            if (muted || volume === null) {
              newVolumes.delete(attendeeId);
            } else {
              newVolumes.set(attendeeId, volume);
            }
            return newVolumes;
          });

          if (muted !== null && muted !== undefined) {
            this.#roster.update((roster) => roster.map((a) => (a.chimeAttendeeId === attendeeId ? { ...a, isMuted: muted } : a)));
          }
        });
      } else {
        this.#roster.update((roster) => roster.filter((a) => a.chimeAttendeeId !== attendeeId));
        session.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId);
      }
    });
  }

  private initAudioContextOnGesture(): void {
    if (typeof window === 'undefined') return;
    const resumeAudio = () => {
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
      document.removeEventListener('click', resumeAudio);
      document.removeEventListener('touchstart', resumeAudio);
    };
    document.addEventListener('click', resumeAudio);
    document.addEventListener('touchstart', resumeAudio);
  }

  public playChimeSound(): void {
    try {
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      const audioCtx = this.audioCtx;
      const now = audioCtx.currentTime;

      const osc1 = audioCtx.createOscillator();
      const gain1 = audioCtx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(392, now); // G4
      gain1.gain.setValueAtTime(0.5, now);
      gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

      osc1.connect(gain1);
      gain1.connect(audioCtx.destination);

      const osc2 = audioCtx.createOscillator();
      const gain2 = audioCtx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(523.25, now + 0.12); // C5
      gain2.gain.setValueAtTime(0.5, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.55);

      osc2.connect(gain2);
      gain2.connect(audioCtx.destination);

      osc1.start(now);
      osc1.stop(now + 0.3);

      osc2.start(now + 0.12);
      osc2.stop(now + 0.55);
    } catch (e) {
      console.warn('Could not play chime sound', e);
    }
  }

  private ringbackInterval: any = null;
  private activeRingingOscillators: OscillatorNode[] = [];

  public playRingingSound(): void {
    if (this.ringbackInterval) return;
    try {
      if (!this.audioCtx) {
        this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      const playTone = () => {
        if (!this.audioCtx) return;
        const audioCtx = this.audioCtx;
        const now = audioCtx.currentTime;

        const osc1 = audioCtx.createOscillator();
        const osc2 = audioCtx.createOscillator();
        const gain = audioCtx.createGain();

        osc1.type = 'sine';
        osc1.frequency.value = 440;
        osc2.type = 'sine';
        osc2.frequency.value = 480;

        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.5, now + 0.1);
        gain.gain.setValueAtTime(0.5, now + 1.9);
        gain.gain.linearRampToValueAtTime(0, now + 2.0);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(audioCtx.destination);

        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 2.0);
        osc2.stop(now + 2.0);

        this.activeRingingOscillators.push(osc1, osc2);
        setTimeout(() => {
          this.activeRingingOscillators = this.activeRingingOscillators.filter((o) => o !== osc1 && o !== osc2);
        }, 2000);
      };

      playTone();
      this.ringbackInterval = setInterval(playTone, 6000);
    } catch (e) {
      console.warn('Could not play ringing sound', e);
    }
  }

  public stopRingingSound(): void {
    if (this.ringbackInterval) {
      clearInterval(this.ringbackInterval);
      this.ringbackInterval = null;
    }
    this.activeRingingOscillators.forEach((osc) => {
      try {
        osc.stop();
      } catch (e) {}
    });
    this.activeRingingOscillators = [];
  }

  public playHangupSound(): void {
    this.stopRingingSound();
    try {
      if (!this.audioCtx) return;
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }
      const audioCtx = this.audioCtx;
      const now = audioCtx.currentTime;

      const playBeep = (startTime: number) => {
        const osc1 = audioCtx.createOscillator();
        const osc2 = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc1.type = 'sine';
        osc1.frequency.value = 480;
        osc2.type = 'sine';
        osc2.frequency.value = 620;

        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(0.3, startTime + 0.05);
        gain.gain.setValueAtTime(0.3, startTime + 0.2);
        gain.gain.linearRampToValueAtTime(0, startTime + 0.25);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(audioCtx.destination);

        osc1.start(startTime);
        osc2.start(startTime);
        osc1.stop(startTime + 0.25);
        osc2.stop(startTime + 0.25);
      };

      playBeep(now);
      playBeep(now + 0.5);
      playBeep(now + 1.0);
    } catch (e) {
      console.warn('Could not play hangup sound', e);
    }
  }

  #startActiveSpeakerDetector(): void {
    this.#activeSpeakerInterval = setInterval(() => {
      const volumes = this.#speakerVolumes();
      let maxVolume = 0;
      let activeSpeaker: string | null = null;

      for (const [attendeeId, volume] of volumes.entries()) {
        if (volume > maxVolume) {
          maxVolume = volume;
          activeSpeaker = attendeeId;
        }
      }

      if (this.#activeSpeakerId() !== activeSpeaker) {
        this.#activeSpeakerId.set(activeSpeaker);
      }
    }, 250); // Check 4 times per second
  }

  private startDurationTimer(startTime?: string | Date): void {
    this.stopDurationTimer(); // Ensure any existing timer is cleared
    const start = startTime ? new Date(startTime) : new Date();
    this.#meetingStartTime.set(start);
    this.meetingDurationText.set('00:00');

    this.durationInterval = setInterval(() => {
      const start = this.#meetingStartTime();
      if (!start) return;

      const diffMs = new Date().getTime() - start.getTime();
      const diffSecs = Math.floor(diffMs / 1000);

      const hours = Math.floor(diffSecs / 3600);
      const minutes = Math.floor((diffSecs % 3600) / 60);
      const seconds = diffSecs % 60;

      const pad = (num: number) => String(num).padStart(2, '0');

      if (hours > 0) {
        this.meetingDurationText.set(`${pad(hours)}:${pad(minutes)}:${pad(seconds)}`);
      } else {
        this.meetingDurationText.set(`${pad(minutes)}:${pad(seconds)}`);
      }
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
    this.#meetingStartTime.set(null);
    this.meetingDurationText.set('00:00');
  }

  #stopActiveSpeakerDetector(): void {
    if (this.#activeSpeakerInterval) {
      clearInterval(this.#activeSpeakerInterval);
      this.#activeSpeakerInterval = null;
    }
  }

  #cleanUp(): void {
    this.stopMeetingTranscription();
    this.stopGeminiLiveAssistant();
    if (this.#videoPreviewIsActive()) {
      this.stopVideoPreview();
    }
    this.#stopActiveSpeakerDetector();
    this.stopDurationTimer();
    this.clearAloneTimer();
    this.#meetingSession.set(null);
    this.#localTileId.set(null);
    this.#remoteTiles.set([]);
    this.#roster.set([]);
    this.#isMuted.set(true);
    this.#isCameraEnabled.set(false);
    this.#chatMessages.set([]);
    this.#unreadCount.set(0);
    this.isChatVisible.set(false);
    this.#screenShareTile.set(null);
    // New cleanup logic for video FX
    this.#videoTransformDevice()?.stop();
    this.#videoTransformDevice.set(null);
    this.#videoFxProcessor()?.destroy();
    this.#videoFxProcessor.set(null);
    this.#isVideoFxActive.set(false);
    this.#isBlurEnabled.set(false);
    this.#speakerVolumes.set(new Map());
    this.#activeSpeakerId.set(null);
    this.#isRecording.set(false);
    this.#isDialing.set(false);
    this.#hostAttendeeIds.set(new Set());
    this.videoCallStateService.closeWhiteboard();
    this.videoCallStateService.preventDrawerClose.set(false);
    this.videoCallStateService.closeDrawer();
    this.videoCallStateService.activeCallId.set(null);
  }

  public getMeetingSession(): MeetingSession | null {
    return this.#meetingSession();
  }

  public sendDataMessage(topic: string, data: any, lifetimeMs?: number): void {
    const session = this.#meetingSession();
    if (session && session.audioVideo) {
      try {
        const payload = typeof data === 'string' ? data : JSON.stringify(data);
        // AWS Chime SDK limit is 2KB. Exceeding this causes the signaling WebSocket to drop.
        if (payload.length > 2000) {
          this.logger.warn(`Data message on topic ${topic} exceeds 2KB limit (length: ${payload.length}). Dropping to prevent disconnect.`);
          return;
        }
        session.audioVideo.realtimeSendDataMessage(topic, payload, lifetimeMs || 30000);
      } catch (error) {
        this.logger.error(`Failed to send data message on topic ${topic}: ${error}`);
      }
    }
  }

  public subscribeToDataMessage(topic: string, callback: (data: any, senderAttendeeId: string) => void): () => void {
    const session = this.#meetingSession();
    if (session && session.audioVideo) {
      const handler = (dataMessage: DataMessage) => {
        try {
          const decoded = new TextDecoder().decode(dataMessage.data);
          let parsed;
          try {
            parsed = JSON.parse(decoded);
          } catch {
            parsed = decoded;
          }
          callback(parsed, dataMessage.senderAttendeeId);
        } catch (err) {
          this.logger.error(`Failed to decode data message on topic ${topic}: ${err}`);
        }
      };
      session.audioVideo.realtimeSubscribeToReceiveDataMessage(topic, handler);
      return () => {
        const activeSession = this.#meetingSession();
        if (activeSession && activeSession.audioVideo) {
          activeSession.audioVideo.realtimeUnsubscribeFromReceiveDataMessage(topic);
        }
      };
    }
    return () => {};
  }

  #updateMuteStatus(attendeeIds: string[], isMuted: boolean): void {
    this.#roster.update((currentRoster) => {
      return currentRoster.map((attendee) => (attendeeIds.includes(attendee.chimeAttendeeId) ? { ...attendee, isMuted } : attendee));
    });
  }

  closeChatPane() {
    this.videoCallStateService.closeChatPane();
  }

  openChatPane() {
    console.log('Opening chat pane');
    this.videoCallStateService.openChatPane();
  }

  openDrawer() {
    this.videoCallStateService.openDrawer();
  }

  closeDrawer() {
    this.videoCallStateService.closeDrawer();
  }

  toggleDrawer() {
    this.videoCallStateService.toggleDrawer();
  }

  openWhiteboard(): void {
    this.videoCallStateService.openWhiteboard();
    if (this.isHost() && this.isMeetingActive()) {
      this.sendDataMessage('moderation', { action: 'toggle-whiteboard', opened: true });
    }
  }

  closeWhiteboard(): void {
    this.videoCallStateService.closeWhiteboard();
    if (this.isHost() && this.isMeetingActive()) {
      this.sendDataMessage('moderation', { action: 'toggle-whiteboard', opened: false });
    }
  }

  toggleWhiteboard(): void {
    const nextState = !this.videoCallStateService.whiteboardOpened();
    if (nextState) {
      this.videoCallStateService.openWhiteboard();
    } else {
      this.videoCallStateService.closeWhiteboard();
    }
    if (this.isHost() && this.isMeetingActive()) {
      this.sendDataMessage('moderation', { action: 'toggle-whiteboard', opened: nextState });
    }
  }

  async startMeetingTranscription(callID: string, geminiInteraction?: string): Promise<void> {
    const audioInputId = this.#selectedAudioInputId();
    await this.meetingTranscriptionService.start(callID, this.#meetingAudioElement, audioInputId, geminiInteraction || '', (deviceId) => this.getAudioConstraints(deviceId));
  }

  async startGeminiLiveAssistant(callID: string): Promise<void> {
    await this.meetingTranscriptionService.startGeminiLive(callID);
  }

  stopMeetingTranscription(): void {
    this.meetingTranscriptionService.stop();

    if (this.#meetingAudioElement) {
      try {
        this.#meetingAudioElement.remove();
        console.log('[ChimeService] Meeting audio element removed from DOM.');
      } catch (e) {
        console.error('[ChimeService] Error removing meeting audio element from DOM:', e);
      }
      this.#meetingAudioElement = null;
    }
  }

  async toggleGeminiLiveAssistant(callID: string): Promise<void> {
    if (this.meetingTranscriptionService.isGeminiLiveActive()) {
      this.stopGeminiLiveAssistant();
    } else {
      await this.startGeminiLiveAssistant(callID);
    }
  }

  stopGeminiLiveAssistant(): void {
    this.meetingTranscriptionService.stopGeminiLive();
  }
}
